/**
 * OceanApiLogs.jsx — Admin / Ocean API Logs module
 *
 * Viewer for the ocean rate-search & booking audit trail
 * (models/oceanQueryLog.model.js): every Freightify /v4/prices call and every
 * booking / booking-confirm / booking-update / booking-cancel outcome, with the
 * exact request/response pair. The create-booking call and the KAM's confirm
 * step are logged as separate types, so they count and filter apart.
 *
 * Two views over the same filtered slice — "Log Details" (grouped by query)
 * and "User-wise Count" — plus the stat cards. All three are separate reads
 * that take the SAME server filters, so the numbers always agree with the rows.
 *
 * Filtering AND paging are server-side. Filters apply as the fields change — no
 * Search button — debounced so typing an id is one request, not one per
 * keystroke; changing page or page size re-reads just the table. A page of the
 * table is a page of QUERIES: the server groups each query's attempts and
 * returns only the collapsed rows on screen, so expanding one costs nothing.
 *
 * This screen used to fetch every matching row (limit=2000) and group, filter
 * and page in the browser. That made the listing the heaviest read in the admin
 * — enough that mongo gave up sorting it — so all four now happen server-side.
 * Only the User-wise tab's search and sort stay local: its aggregation returns
 * every matching user in one response.
 */
import { Pagination, Spin } from "antd";
import dayjs from "dayjs";
import { Download, RefreshCw, Ship, TriangleAlert } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomeHeader from "../../components/Admin/Common/CustomeHeader";
import LogDetailModal from "../../components/Admin/OceanApiLogs/LogDetailModal";
import LogFilterBar from "../../components/Admin/OceanApiLogs/LogFilterBar";
import LogStatCards from "../../components/Admin/OceanApiLogs/LogStatCards";
import LogTable from "../../components/Admin/OceanApiLogs/LogTable";
import UserFilterBar from "../../components/Admin/OceanApiLogs/UserFilterBar";
import UserWiseCountTable from "../../components/Admin/OceanApiLogs/UserWiseCountTable";
import {
  baseTypeOf,
  collectShippingLines,
  formatCreatedAt,
  fullRequestUrl,
  getVariantMeta,
  isErrorVariant,
  matchesUserSearch,
  normalizeGroup,
  normalizeStats,
  normalizeUserCount,
  sortUserRows,
} from "../../components/Admin/OceanApiLogs/logUtils";
import {
  DEFAULT_PAGE_SIZE,
  EXPORT_GROUP_LIMIT,
  PAGE_SIZE_OPTIONS,
  fetchAllGroupedLogs,
  fetchGroupedLogs,
  fetchLogStats,
  fetchShippingLines,
  fetchUserCounts,
} from "../../components/Admin/OceanApiLogs/oceanLogApi";
import useExcel from "../../utils/generateExcelFile";

/** Long enough to finish typing/pasting an ObjectId before the read fires. */
const FILTER_DEBOUNCE_MS = 450;

const EMPTY_FILTERS = {
  search: "",
  userId: "",
  type: "all",
  shippingLine: "",
  from: "",
  to: "",
};

/** The user tab's own controls — searched and sorted locally, never sent. */
const EMPTY_USER_FILTERS = { search: "", sortBy: "total" };

const EMPTY_STATS = {
  total: 0,
  failed: 0,
  rate_search: 0,
  confirmation_awaited: 0,
  bookings: 0,
  new_bookings: 0,
  booking_updates: 0,
  booking_cancel: 0,
};

/**
 * UI filter state → the query params buildLogFilter reads. `from`/`to` come off
 * native date inputs as `YYYY-MM-DD`, so each end is widened to cover its whole
 * day before being sent; either end alone is a valid open-ended range.
 *
 * Every filter is sent — including the two that used to be applied in the
 * browser. A type selection is split into the pair the API takes: its BASE type,
 * plus `failedOnly` for the ":error" variants ("Ocean Booking Error").
 */
const toServerFilters = (filters) => ({
  search: filters.search.trim() || undefined,
  userId: filters.userId || undefined,
  type: filters.type !== "all" ? baseTypeOf(filters.type) : undefined,
  failedOnly: isErrorVariant(filters.type) ? true : undefined,
  shippingLine: filters.shippingLine || undefined,
  from: filters.from ? dayjs(filters.from).startOf("day").toISOString() : undefined,
  to: filters.to ? dayjs(filters.to).endOf("day").toISOString() : undefined,
});

const countActiveFilters = (filters) =>
  Object.entries(filters).filter(([key, value]) => {
    if (key === "type") return value && value !== "all";
    return Boolean(value);
  }).length;

function OceanApiLogs() {
  const { generateExcelFile } = useExcel();

  const [tab, setTab] = useState("logs");
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [userRows, setUserRows] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [userFilters, setUserFilters] = useState(EMPTY_USER_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Filters fire on change, so reads can overlap; only the newest one may write
  // to state. `firstLoad` keeps the debounced effect off the mount fetch, and
  // `lastFilterKey` is what tells a page change apart from a filter change.
  const requestSeq = useRef(0);
  const firstLoad = useRef(true);
  const lastFilterKey = useRef(null);

  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // The line dropdown's options, from their own read — the table only ever
  // holds one page now, so it can no longer supply them on its own.
  const [serverLineOptions, setServerLineOptions] = useState([]);

  /**
   * One load = the table's page, plus the two aggregations under the same
   * filters. They're settled independently so a failing aggregation doesn't
   * blank the table (and vice versa) — whatever came back still renders, and
   * the banner names the rest.
   *
   * `logsOnly` is the paging case: turning a page or resizing it re-reads the
   * table, but the cards and the user tab describe the whole filtered slice and
   * don't change with it.
   */
  const loadAll = async (activeFilters, { page = 1, size = pageSize, seedUserOptions = false, logsOnly = false } = {}) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    const params = toServerFilters(activeFilters);

    const [logsResult, statsResult, usersResult] = await Promise.allSettled([
      fetchGroupedLogs(params, { page, limit: size }),
      logsOnly ? Promise.resolve(null) : fetchLogStats(params),
      logsOnly ? Promise.resolve(null) : fetchUserCounts(params),
    ]);

    // A newer filter change already went out — let that one land instead (it
    // owns `loading` too, so bailing here can't leave the spinner stuck).
    if (seq !== requestSeq.current) return;

    if (logsResult.status === "fulfilled") {
      setGroups(logsResult.value.groups.map(normalizeGroup));
      setPagination(logsResult.value.pagination);
    } else {
      setGroups([]);
      setPagination(null);
    }

    if (!logsOnly) {
      setStats(statsResult.status === "fulfilled" ? normalizeStats(statsResult.value) : EMPTY_STATS);

      if (usersResult.status === "fulfilled") {
        const rows = usersResult.value.map(normalizeUserCount);
        setUserRows(rows);
        // The "Requested by" dropdown is seeded from the unfiltered load only —
        // rebuilding it from a filtered response would remove the very options
        // needed to widen the filter again.
        if (seedUserOptions) {
          setUserOptions(
            rows
              .filter((row) => row.user_id)
              .map((row) => ({ value: row.user_id, label: row.email ? `${row.requested_by} · ${row.email}` : row.requested_by }))
          );
        }
      } else {
        setUserRows([]);
      }
    }

    const failures = [
      logsResult.status === "rejected" && "logs",
      statsResult.status === "rejected" && "stats",
      usersResult.status === "rejected" && "user counts",
    ].filter(Boolean);

    if (failures.length) {
      const firstError = [logsResult, statsResult, usersResult].find((result) => result.status === "rejected")?.reason;
      setLoadError({
        parts: failures,
        message: firstError?.response?.data?.message || firstError?.message || "Request failed",
      });
    } else {
      setLoadError(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadAll(EMPTY_FILTERS, { page: 1, size: DEFAULT_PAGE_SIZE, seedUserOptions: true });

    // Unfiltered and fetched once, for the same reason the user dropdown is:
    // options built from a narrowed read would drop the ones needed to widen
    // the filter again. A line with nothing in the current slice simply comes
    // back empty when picked.
    fetchShippingLines()
      .then(setServerLineOptions)
      .catch(() => setServerLineOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** A filter change is always a new first page — the old one may not exist. */
  const handleFiltersChange = (next) => {
    setFilters(next);
    setCurrentPage(1);
  };

  // Every filter reaches the server now, so this key covers all of them.
  const serverFilterKey = useMemo(() => JSON.stringify(toServerFilters(filters)), [filters]);

  // One effect drives every read after mount. Both state updates from
  // handleFiltersChange land in the same render, so a filter change fires this
  // once (on page 1), not twice.
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      lastFilterKey.current = serverFilterKey;
      return undefined;
    }

    const filtersChanged = lastFilterKey.current !== serverFilterKey;
    lastFilterKey.current = serverFilterKey;

    // Debounce typing; a pager click is deliberate and shouldn't wait.
    const delay = filtersChanged ? FILTER_DEBOUNCE_MS : 0;
    const timer = setTimeout(
      () => loadAll(filters, { page: currentPage, size: pageSize, logsOnly: !filtersChanged }),
      delay
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFilterKey, currentPage, pageSize]);

  /**
   * The line dropdown.
   *
   * Its own read supplies the breadth (the newest window across the whole
   * collection); the page on screen is unioned in because a booking's line is
   * derived in the browser from the logged payload, so those never appear in the
   * server list. A selection that neither still contains is kept as an option,
   * labelled from the remembered display name — otherwise the Select would
   * render its own value, a lowercased key, with no way to read or clear it.
   */
  const lineLabels = useRef(new Map());

  const lineOptions = useMemo(() => {
    const byValue = new Map();
    [...serverLineOptions, ...collectShippingLines(groups)].forEach((option) => {
      if (!byValue.has(option.value)) byValue.set(option.value, option);
      lineLabels.current.set(option.value, option.label);
    });

    const selected = filters.shippingLine;
    if (selected && !byValue.has(selected)) {
      byValue.set(selected, { value: selected, label: lineLabels.current.get(selected) || selected });
    }

    return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [serverLineOptions, groups, filters.shippingLine]);

  /**
   * Every row on screen is already the filtered, paged slice the server sent —
   * shipping line and errors-only included — so nothing is narrowed here and
   * the cards and user tab describe exactly the same slice.
   */
  const allLogs = useMemo(() => groups.flatMap((group) => group.logs), [groups]);

  /**
   * The user tab's rows as it shows them. Its search and sort have no server
   * param (the aggregation returns every matching user at once), so both are
   * applied here — and to the export, so a download matches the screen.
   */
  const displayUserRows = useMemo(
    () => sortUserRows(userRows.filter((row) => matchesUserSearch(row, userFilters.search)), userFilters.sortBy),
    [userRows, userFilters.search, userFilters.sortBy]
  );

  // A page or filter change replaces the rows an open group belonged to, so
  // drop expansions that no longer have a row on screen.
  useEffect(() => {
    const visibleKeys = groups.map((group) => group.key);
    setExpandedKeys((keys) => keys.filter((key) => visibleKeys.includes(key)));
  }, [groups]);

  const handleToggleGroup = (key) =>
    setExpandedKeys((keys) => (keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]));

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setModalOpen(true);
  };

  /**
   * The export covers the whole filtered match, not the page on screen — so the
   * logs tab walks the listing server-side first. That read is the one the
   * screen used to do on every filter change; now it only happens on a click.
   */
  const handleDownloadExcel = async () => {
    let exportLogs = allLogs;

    if (tab === "logs") {
      setExporting(true);
      try {
        const { groups: allGroups, truncated } = await fetchAllGroupedLogs(toServerFilters(filters));
        exportLogs = allGroups.map(normalizeGroup).flatMap((group) => group.logs);
        if (truncated) {
          toast.info(`Exporting the latest ${EXPORT_GROUP_LIMIT.toLocaleString("en-IN")} queries — narrow the filters for the rest.`);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Could not build the export.");
        setExporting(false);
        return;
      }
      setExporting(false);
    }

    const rows =
      tab === "logs"
        ? exportLogs.map((log, index) => ({
            "S.No": index + 1,
            "Log ID": log.id,
            "Query ID": log.query_id || "",
            Origin: log.origin || "",
            Destination: log.destination || "",
            Carriers: log.carriers.join(", "),
            Offers: log.offers_count ?? "",
            Type: getVariantMeta(log.variant).label,
            "Container Load": log.container_load || "",
            Containers: log.total_containers ?? "",
            "Weight (Kg)": log.total_weight ?? "",
            "Requested By": log.requested_by,
            Email: log.requested_by_email || "",
            Status: log.failed ? "Failed" : "Success",
            "Status Code": log.status_code ?? "",
            Method: log.method || "",
            "Requested URL": fullRequestUrl(log),
            Created: formatCreatedAt(log.created_at),
          }))
        : displayUserRows.map((row, index) => ({
            "S.No": index + 1,
            User: row.requested_by,
            Email: row.email || "",
            "Rate Search": row.rate_search,
            "Confirmation Awaited": row.confirmation_awaited,
            Booking: row.bookings + row.booking_updates,
            "New Bookings": row.bookings,
            "Booking Updates": row.booking_updates,
            Cancelled: row.booking_cancel,
            Total: row.total,
          }));

    if (!rows.length) {
      toast.info("Nothing to export for the current filters.");
      return;
    }

    const suffix = tab === "logs" ? "log-details" : "user-wise-count";
    generateExcelFile(rows, "Ocean API Logs", `ocean-api-logs-${suffix}-${dayjs().format("DDMMYYYY")}.xlsx`);
  };

  const tabs = [
    { key: "logs", label: "Log Details" },
    { key: "users", label: "User-wise Count" },
  ];

  return (
    <div className="pb-10">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar newestOnTop />

      {/* App bar (avatar / notifications) — air_admin pages render this
          themselves; PageLayout only mounts <Header /> for the other roles. */}
      <CustomeHeader title="Ocean API Logs" />

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 pt-6 sm:flex-col">
        <div className="flex items-center gap-3.5">
          <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary shadow-[0px_6px_16px_rgba(252,182,20,0.35)]">
            <Ship size={22} className="text-white" />
          </span>
          <div>
            <h1 className="text-[26px] leading-8 font-bold text-[#1E293B]">Ocean API Logs</h1>
            <p className="mt-0.5 text-[13px] text-gray-500">
              Track Freightify rate search, booking &amp; cancellation activity — by log or by user
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => loadAll(filters, { page: currentPage, size: pageSize })}
            disabled={loading}
            className="h-11 px-5 flex items-center gap-2 rounded-lg bg-white border border-gray-200 text-[14px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleDownloadExcel}
            disabled={exporting}
            className="h-11 px-5 flex items-center gap-2 rounded-lg bg-[#16A34A] text-[14px] font-semibold text-white hover:bg-[#128A3E] disabled:opacity-60"
          >
            <Download size={16} />
            {exporting ? "Preparing…" : "Download Excel"}
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mt-5 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA]">
          <TriangleAlert size={17} className="mt-0.5 text-[#DC2626]" />
          <p className="text-[13px] text-[#991B1B]">
            <span className="font-semibold">Could not load {loadError.parts.join(", ")}.</span> {loadError.message} —
            use Refresh to try again.
          </p>
        </div>
      )}

      <div className="mt-6">
        <LogStatCards stats={stats} />
      </div>

      {/* View switch */}
      <div className="mt-6 inline-flex items-center gap-1 p-1.5 rounded-xl bg-[#FFF8E7] border border-[#F6E3B4]">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`px-5 py-2.5 rounded-lg text-[14px] font-semibold transition-colors ${
              tab === item.key ? "bg-white text-[#1E293B] shadow-sm" : "text-[#B08324] hover:text-[#8A6A12]"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "logs" ? (
          <LogFilterBar
            open={filterOpen}
            onToggle={() => setFilterOpen((value) => !value)}
            filters={filters}
            onChange={handleFiltersChange}
            userOptions={userOptions}
            lineOptions={lineOptions}
            activeCount={countActiveFilters(filters)}
          />
        ) : (
          <UserFilterBar search={userFilters.search} sortBy={userFilters.sortBy} onChange={setUserFilters} />
        )}
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="flex items-center justify-center h-[320px] rounded-xl bg-white border border-gray-200">
            <Spin size="large" />
          </div>
        ) : tab === "logs" ? (
          <>
            <LogTable
              groups={groups}
              expandedKeys={expandedKeys}
              onToggleGroup={handleToggleGroup}
              onViewLog={handleViewLog}
            />
            <div className="flex items-center justify-between mt-4 sm:flex-col sm:gap-3">
              {/* The whole match, not the page: `total` counts the matching
                  queries and `totalLogs` the attempts inside them. */}
              <p className="text-[13px] text-gray-500">
                {(pagination?.total ?? 0).toLocaleString("en-IN")} {pagination?.total === 1 ? "query" : "queries"} ·{" "}
                {(pagination?.totalLogs ?? allLogs.length).toLocaleString("en-IN")} logs
                <span className="text-gray-400"> · showing {groups.length} on this page</span>
              </p>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={pagination?.total ?? 0}
                showSizeChanger
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                // Paging is a server read now — the effect above fires on these.
                onChange={(page, size) => {
                  setCurrentPage(size !== pageSize ? 1 : page);
                  setPageSize(size);
                }}
              />
            </div>
          </>
        ) : (
          <UserWiseCountTable rows={displayUserRows} />
        )}
      </div>

      <LogDetailModal log={selectedLog} open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}

export default OceanApiLogs;
